<template>
  <div ref="dropdownRoot">
    <button @click="toggleDropdown" class="group">
      <span
        v-if="text"
        :class="[
          'text-sm lg:text-base flex items-center justify-center cursor-pointer px-2 lg:px-4 py-4 h-full font-semibold border-b-3',
          open
            ? 'border-primary'
            : 'border-transparent group-hover:border-surface-grey',
        ]"
      >
        {{ text }}

        <DropdownIcon class="ml-2 h-3 w-3" />
      </span>
      <slot name="trigger" />
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          class="fixed inset-0 bg-accent/50 z-40 cursor-pointer transition-opacity"
          @click="closeDropdown"
          @touchend.prevent="closeDropdown"
        />
      </Transition>
    </Teleport>

    <BaseSlideTransition>
      <div v-show="open" class="absolute top-full left-0 w-full z-50">
        <div
          :class="[
            'md:py-6 border-b border-surface-accent-light',
            props.backgroundClass
              ? props.backgroundClass
              : 'bg-surface-accent-lightest ',
          ]"
        >
          <BaseContainer>
            <slot />
          </BaseContainer>
        </div>
      </div>
    </BaseSlideTransition>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import DropdownIcon from '../icons/DropdownIcon.vue';
  import BaseContainer from '../ui/BaseContainer.vue';
  import BaseSlideTransition from './BaseSlideTransition.vue';

  const props = defineProps<{
    open?: boolean;
    backgroundClass?: string;
    text?: string;
  }>();
  const emit = defineEmits(['onChange']);

  const open = ref(!!props.open);
  const dropdownRoot = ref<HTMLElement | null>(null);

  function closeDropdown() {
    if (!open.value) return;
    open.value = false;
    emit('onChange', false);
  }

  defineExpose({
    closeDropdown,
  });

  function toggleDropdown() {
    open.value = !open.value;
    emit('onChange', open.value);
  }

  function handleClickOutside(event: MouseEvent) {
    if (!open.value) return;

    if (
      dropdownRoot.value &&
      !dropdownRoot.value.contains(event.target as Node)
    ) {
      open.value = false;
      emit('onChange', false);
    }
  }

  watch(
    () => props.open,
    val => {
      open.value = !!val;
    }
  );

  onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside);
  });
</script>
